import { useEffect, useId, useState } from "react";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock.js";
import "./product-filter.css";

const MOBILE_FILTER_MQ = "(max-width: 1100px)";

/**
 * Shared chrome for product/order filter sidebars: sticky on desktop,
 * fixed lower-left toggle + scrollable bottom sheet on mobile.
 */
export default function FilterToolboxShell({
    loading = false,
    onApply,
    onReset,
    children
}) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const titleId = useId();
    const panelHiddenOnMobile = isMobileLayout && !mobileOpen;

    const closeMobile = () => setMobileOpen(false);

    useEffect(() => {
        const media = window.matchMedia(MOBILE_FILTER_MQ);
        const sync = () => {
            const matches = media.matches;
            setIsMobileLayout(matches);
            if (!matches) {
                setMobileOpen(false);
            }
        };
        sync();
        media.addEventListener("change", sync);
        return () => media.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        if (!mobileOpen) {
            return undefined;
        }
        lockBodyScroll();
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setMobileOpen(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            unlockBodyScroll();
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [mobileOpen]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        await onApply();
        setMobileOpen(false);
    };

    const handleReset = async () => {
        await onReset();
        setMobileOpen(false);
    };

    return (
        <>
            <button
                type="button"
                className={`product-filter-fab${mobileOpen ? " is-open" : ""}`}
                aria-expanded={mobileOpen}
                aria-controls={titleId}
                data-tour="catalog-filters-fab"
                onClick={() => setMobileOpen((open) => !open)}
            >
                {mobileOpen ? "Fermer" : "Chercher"}
            </button>

            <div
                className={`product-filter-backdrop${mobileOpen ? " is-open" : ""}`}
                onClick={closeMobile}
                aria-hidden="true"
            />

            <aside
                id={titleId}
                className={`product-filter-toolbox card${mobileOpen ? " is-open" : ""}`}
                aria-hidden={panelHiddenOnMobile ? true : undefined}
                data-tour="catalog-filters"
                {...(panelHiddenOnMobile ? { inert: "" } : {})}
            >
                <div className="product-filter-toolbox-header" data-tour="catalog-filters-header">
                    <h2>Recherche</h2>
                    <button
                        type="button"
                        className="product-filter-close"
                        onClick={closeMobile}
                        aria-label="Fermer la recherche"
                    >
                        ×
                    </button>
                </div>
                <form className="product-filter-form" onSubmit={handleSubmit}>
                    {typeof children === "function" ? children({ closeMobile }) : children}
                    <div className="product-filter-actions">
                        <button className="btn btn-primary btn-inline" type="submit" disabled={loading}>
                            {loading ? "Application..." : "Appliquer"}
                        </button>
                        <button
                            className="btn btn-secondary btn-inline"
                            type="button"
                            onClick={handleReset}
                            disabled={loading}
                        >
                            Réinitialiser
                        </button>
                    </div>
                </form>
            </aside>
        </>
    );
}
